/* eslint-disable complexity */
import {
    ChangeStateKey, ChangeTypeKey, PERSON_CHANGE_STEP, PIPELINE_TYPE,
    QUALIFY_STATUS,
} from '@/dicts/qualify';
import {checkInChangeProcess} from '@/modules/Change/components/changeUtil';
import {ChangeInfo} from '@/types/api/change';
import {QualifyInfo} from '@/types/api/qualify';
import {getParams} from '@/utils/util';

/**
 * 当前认证状态是否允许个人变更
 * @param info
 * @returns
 */
function checkInfoAllowPersonChange(info: QualifyInfo) {
    // 老个人转企业审核中，不允许变更
    const isOldPerToCompAudit = info.hasPassPersonPrime
    && info?.company?.enterpriseSeniorStatus === QUALIFY_STATUS.AUDIT;
    const infoAllowChange = (
    // 个人初级通过
        (info.person && info.person.status === QUALIFY_STATUS.PASS)
    // 老个人转企业失败，个人初级通过
    || (info.hasPassPersonPrime && info?.company?.status === QUALIFY_STATUS.RETURN)
    ) && !isOldPerToCompAudit;
    return infoAllowChange;
}

function checkInfoAllowCompChange(info: QualifyInfo) {
    // 企业进行工商变更
    const compChangeEnable = info.company?.enterpriseSeniorStatus === QUALIFY_STATUS.PASS
        || info.hasPassCorporationPrime;
    return info?.changeInfo?.inNameChangeWhiteList && compChangeEnable;
}

function checkPersonChangeAllow(
    enableChange: boolean,
    actionName: string,
    inChange: boolean,
    changeInfo: any,
    search: string
) {
    // 允许变更时，可以直接访问变更协议页面
    const changePage = [
        'change/person',
        'change/company',
        'change/result',
        'change/log',
    ].includes(actionName);
    if (changePage) {
        return actionName;
    }

    const changeAllowPage = [
        'choices/person',
        'choices/company',
        'person/face',
        'person/bank',
        'person/doc',
        'company/recharge',
        'company/remit',
        'company/legal',
    ].includes(actionName);
    if (enableChange && inChange && changeAllowPage) {
        const params = getParams(search);
        const after = checkInChangeProcess(changeInfo, params);
        if (after) {
            return after;
        }
        return `${actionName}?${search}`;
    }
}

function checkCompChangeAllow(
    actionName: string,
    info?: QualifyInfo,
    search?: string
) {
    const inChange = search && /(change=|current=)/.test(search);
    // 企业初级且未高级
    if (info?.hasPassCorporationPrime && info.company?.enterpriseSeniorStatus !== QUALIFY_STATUS.PASS) {
        // 允许企业初级变更时，不允许直接访问高级的企业名称变更
        const noAllowPage = [
            'change/registration',
        ].includes(actionName);
        if (noAllowPage) {
            return `result${search ? '?' + search : ''}`;
        }
        // 企业初级账号，不允许访问变更列表，自动跳转到企业主体变更
        if (actionName === 'change/list') {
            const {changeInfo} = info;
            // 非工商变更-原主体已注销
            const isNoBusiness = changeInfo?.isNoBusiness;
            return `change/enterprise${isNoBusiness ? '?changeType=revocation' : ''}`;
        }
    }
    else {
        // 允许企业高级变更时，可以直接访问的页面
        const changePage = [
            'change/registration',
            'change/enterprise',
            'change/org/entity',
            'change/list',
            'change/result',
            'change/log',
        ];
        if (changePage.includes(actionName)) {
            return `${actionName}${search ? '?' + search : ''}`;
        }

        const changeAllowPage = [
            'choices/company',
            'company/recharge',
            'company/remit',
            'company/legal',
        ].includes(actionName);
        if (changeAllowPage && inChange) {
            const params = getParams(search);
            const after = checkInChangeProcess(info?.changeInfo, params);
            if (after) {
                return after;
            }
            return `${actionName}?${search}`;
        }
    }
}

function specialAuditRedirect(
    pipelineType?: PIPELINE_TYPE,
    change?: ChangeTypeKey,
    current?: ChangeStateKey
) {
    const changePrefix = change && current ? `?current=${current}&change=${change}` : '';
    switch (pipelineType) {
        case PIPELINE_TYPE.TRANSFER_TO_CORPORATION_UNION_PAY:
            return `company/remit${changePrefix}`;
        case PIPELINE_TYPE.CORPORATION_TO_BAIDU_PAYMENT:
            return `company/recharge${changePrefix}`;
        default:
            return '';
    }
}

export function redirectCompChangeAudit(changeInfo?: ChangeInfo) {
    const {status = ''} = changeInfo?.originalMainBody || {};
    if (status === 'HUMAN_AUDIT') {
        return 'change/result';
    } else if (['AUDIT', 'SUBMIT'].includes(status)) {
        // 非工商实名变更-原主体已注销
        if (changeInfo?.isNoBusiness) {
            return 'company/noBusiness/change';
        }
        return 'change/org/entity';
    }
    if (changeInfo?.newMainBody?.status === 'AUDIT') {
        // 企业变更主体，新主体对公打款流程中和充值认证流程中特殊处理
        return specialAuditRedirect(
            changeInfo?.newMainBody?.pipelineType, 'c2c', 'new');
    }
    return '';
}

export function redirectPersonChangeAudit(changeInfo?: ChangeInfo) {
    const originStatus = changeInfo?.originalMainBody?.status;
    const newStatus = changeInfo?.newMainBody?.status;
    const change =
        changeInfo?.newMainBody?.mainBodyType === 'PERSON'
            ? 'p2p'
            : changeInfo?.newMainBody?.mainBodyType === 'CORPORATION'
                ? 'p2c'
                : '';
    let current = 'origin';
    if (originStatus === 'AUDIT') {
        const pipelineType = changeInfo?.originalMainBody?.pipelineType;
        const after = specialAuditRedirect(pipelineType);
        return after ? `${after}?change=${change}&current=${current}` : 'change/result';
    } else if (newStatus === 'AUDIT') {
        current = 'new';
        const pipelineType = changeInfo?.newMainBody?.pipelineType;
        const after = specialAuditRedirect(pipelineType);
        return after ? `${after}?change=${change}&current=${current}` : 'change/result';
    }
    return '';
}

export function redirectChangeAudit(changeInfo?: ChangeInfo, actionName?: string) {
    // 变更审核中，但是可以访问实名结果页
    if (actionName && [
        'result',
        'change/log',
    ].includes(actionName)) {
        return '';
    }
    const isCompChangeOrigin = changeInfo?.changeType === 'CORPORATION_REAL_CHANGE';
    // 企业变更主体，原主体通过，新主体未认证，不允许访问原主体的认证操作页
    const inNewChange = changeInfo?.status && [
        PERSON_CHANGE_STEP.NEW_RETURN,
        PERSON_CHANGE_STEP.ORIGIN_PASS,
    ].includes(changeInfo.status);
    if (isCompChangeOrigin && actionName && [
        'change/list',
        'change/org/entity',
    ].includes(actionName) && inNewChange) {
        return 'change/result';
    }
    const action = isCompChangeOrigin ? redirectCompChangeAudit(changeInfo)
        : redirectPersonChangeAudit(changeInfo);
    return action;
}

/**
 * 企业初级升高级工商变更审核中跳转
 * @param changeInfo
 * @param actionName
 * @returns
 */
function redirectCompPrimeChangeAudit(changeInfo?: ChangeInfo, actionName?: string) {
    // 变更审核中，但是可以访问变更日志
    const action = redirectChangeAudit(changeInfo, actionName);
    if (action) {
        return action;
    }
    const newStatus = changeInfo?.newMainBody?.status;
    if (newStatus === 'AUDIT') {
        const pipelineType = changeInfo?.newMainBody?.pipelineType;
        const after = specialAuditRedirect(pipelineType);
        return after || 'change/result';
    }

    return '';
}

function redirectPersonChange(
    info: QualifyInfo,
    actionName: string,
    search: string
) {
    // 页面操作带变更状态，允许访问认证操作页和认证列表
    const inChange = /(change=|current=)/.test(search);
    const params: any = getParams(search);
    const changeInfo = info?.changeInfo;
    // 距离上次变更成功30天以上允许变更
    if (changeInfo?.enableChange) {
        // 变更审核中，只允许访问变更审核中页面
        const audit = redirectChangeAudit(info?.changeInfo, actionName);
        if (audit) {
            return audit;
        }
        const allow = checkPersonChangeAllow(
            changeInfo?.enableChange,
            actionName,
            inChange,
            info.changeInfo,
            search
        );
        if (allow) {
            return allow;
        }
    }
    else {
        // 变更通过，30天不允许变更限制内
        if (inChange && params.current === 'new') {
            // 刷脸相关可以自动跳转到结果页
            if (info?.changeInfo?.status === PERSON_CHANGE_STEP.NEW_PASS) {
                return 'change/result';
            }
        }
        if (['change/person', 'change/company'].includes(actionName)) {
            return 'change/result';
        }
    }
}

function redirectCompChange(
    info: QualifyInfo,
    actionName: string,
    search: string
) {
    const changeInfo = info?.changeInfo;
    // 企业变更的时候，无论是不是流程中，资质认证都可以访问
    if (['license/apply'].includes(actionName)) {
        return actionName;
    }
    // 企业初级升高级工商变更的时需要替换调当前的实名状态
    const isPrimeChange = info.hasPassCorporationPrime && info.company
        && ![QUALIFY_STATUS.PASS].includes(info.company.enterpriseSeniorStatus);
    const audit = isPrimeChange
        // 企业初级在变更流程中的跳转处理
        ? redirectCompPrimeChangeAudit(changeInfo, actionName)
        // 变更审核中，只允许访问变更审核中页面
        : redirectChangeAudit(changeInfo, actionName);
    if (audit) {
        return audit;
    }
    const allow = checkCompChangeAllow(
        actionName,
        info,
        search
    );
    if (allow) {
        return allow;
    }
}

// 个人初级通过，高级未通过
// 个人初级通过，高级审核中
// 个人高级通过
// 个人转企业失败，个人初级已通过
export function redirectChange(
    info: QualifyInfo,
    actionName: string,
    search: string
) {
    // 是否允许操作个人变更
    const enablePersonChange = checkInfoAllowPersonChange(info);
    if (enablePersonChange) {
        const allow = redirectPersonChange(info, actionName, search);
        if (allow) {
            return allow;
        }
    }
    // 是否允许操作企业变更
    const enableCompChange = checkInfoAllowCompChange(info);
    if (enableCompChange) {
        const allow = redirectCompChange(info, actionName, search);
        if (allow) {
            return allow;
        }
    }
    if (['change/result', 'change/log'].includes(actionName)) {
        return actionName;
    }
    // 不允许变更，还是按照老流程走
    return '';
}
